import { FigureReorderDialog } from "components/system";
import { useConfirm } from "material-ui-confirm";
import moment from "moment";
import { useModal } from "mui-modal-provider";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import MessageType from "utils/MessageType";
import { useAppContext } from "../../../../context/AuthContext";
import { ArticleController, ArticleControllerType } from "../../../../controller/article/ArticleController";
import LabelsContext from "../../../../lang/LabelsContext";
import { handleErrorMessage, handleFileType, renameFile } from "../../../../utils/Utils";
import EnumDesignation from "../../../../utils/enum/EnumDesignation";

interface TabUploadRevisionServiceType {
  uploadFile: any;
  filesList: any;
  filesByDesignation: any;
  designationOptions: any;
  openDialog: any;
  handleOpenDialog: any;
  handleCloseDialog: any;
  handleResetDialog: any;
  files: any;
  uploading: boolean;
  submit: () => void;
  deleteFile: (fileId: number) => void;
  deleteFileGroup: any;
  selectedDesigantion: any;
  checkAllRelevantDoc: boolean;
  setCheckAllRelevantDoc: any;
  reuseFilesFromOldRevision: (code: string) => void;
}

export default function TabUploadRevisionService({ article }: any): TabUploadRevisionServiceType {
  const { axiosUploadFile, axiosPostSubmitRevision }: ArticleControllerType = ArticleController();
  const { showModal } = useModal();
  const { showMessage, setBlockUI } = useAppContext();
  const { Labels } = LabelsContext();
  const [filesList, setFilesList] = useState<any>([]);
  const [uploading, setUploading] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const { id }: any = useParams();
  const history = useHistory();

  const confirm = useConfirm();

  const [filesByDesignation, setFilesByDesignation] = useState<File[]>([]);

  const [selectedDesigantion, setSelectedDesignation] = useState<any>();

  const [checkAllRelevantDoc, setCheckAllRelevantDoc] = useState<boolean>(false);

  const updateFiles = (fileList: any) => {
    const arrayFile: any = [];
    const isArticleAfterDate = moment.unix(article.createDate).isAfter(moment("2024-03-17", "YYYY-MM-DD"));

    // letter of rebutal
    const fileLetterOfRebutal = fileList?.find((f: any) => f.designation === EnumDesignation.LETTER_OF_REBUTAL);
    arrayFile.push({
      id: fileLetterOfRebutal?.id ?? null,
      name: fileLetterOfRebutal?.name ?? "required*",
      designation: EnumDesignation.LETTER_OF_REBUTAL,
      type: handleFileType(EnumDesignation.LETTER_OF_REBUTAL)?.join(", ").replaceAll(".", ""),
      location: fileLetterOfRebutal?.location ?? null,
      showAdd: !Boolean(fileLetterOfRebutal),
      showDelete: Boolean(fileLetterOfRebutal),
      showSync: false,
      code: EnumDesignation.LETTER_OF_REBUTAL,
      color: fileLetterOfRebutal ? "green" : "red",
      tooltip: "only one letter of rebutal file allowed",
    });

    // marked manuscript
    const fileMarkedManuscript = fileList?.find((f: any) => f.designation === EnumDesignation.MARKED_MANUSCRIPTS);
    arrayFile.push({
      id: fileMarkedManuscript?.id ?? null,
      name: fileMarkedManuscript?.name ?? "required*",
      designation: EnumDesignation.MARKED_MANUSCRIPTS,
      type: handleFileType(EnumDesignation.MARKED_MANUSCRIPTS)?.join(", ").replaceAll(".", ""),
      location: fileMarkedManuscript?.location ?? null,
      showAdd: !Boolean(fileMarkedManuscript),
      showDelete: Boolean(fileMarkedManuscript),
      showSync: false,
      code: EnumDesignation.MARKED_MANUSCRIPTS,
      color: fileMarkedManuscript ? "green" : "red",
      tooltip: "only one marked manuscript file allowed",
    });

    // unmarked manuscript
    const fileUnmarkedManuscript = fileList?.find((f: any) => f.designation === EnumDesignation.UNMARKED_MANUSCRIPT);
    arrayFile.push({
      id: fileUnmarkedManuscript?.id ?? null,
      name: fileUnmarkedManuscript?.name ?? "required*",
      designation: EnumDesignation.UNMARKED_MANUSCRIPT,
      type: handleFileType(EnumDesignation.UNMARKED_MANUSCRIPT)?.join(", ").replaceAll(".", ""),
      location: fileUnmarkedManuscript?.location ?? null,
      showAdd: !Boolean(fileUnmarkedManuscript),
      showDelete: Boolean(fileUnmarkedManuscript),
      showSync: false,
      code: EnumDesignation.UNMARKED_MANUSCRIPT,
      color: fileUnmarkedManuscript ? "green" : "red",
      tooltip: "only one unmarked manuscript file allowed",
    });

    // figure
    const fileFigureArray = fileList?.filter((f: any) => f.designation === EnumDesignation.FIGURE);
    const oldFileFigureArray = article.files?.some(
      (f: any) => f.type === EnumDesignation.FIGURE && Number(f.revision) === Number(article.revision)
    );
    arrayFile.push({
      name: fileFigureArray?.length > 0 ? fileFigureArray?.length : "no files",
      designation: EnumDesignation.FIGURE,
      files: fileFigureArray,
      type: handleFileType(EnumDesignation.FIGURE)?.join(", ").replaceAll(".", ""),
      showAdd: true,
      showSync: fileFigureArray?.length === 0 && oldFileFigureArray && isArticleAfterDate,
      showDelete: fileFigureArray?.length > 0,
      code: EnumDesignation.FIGURE,
      color: fileFigureArray?.length > 0 ? "green" : "black",
    });

    // supplemenatry figure
    const supplementaryFileFigureArray = fileList?.filter(
      (f: any) => f.designation === EnumDesignation.SUPPLEMENTARY_FIGURE
    );
    const oldSupplementaryFileFigureArray = article.files?.some(
      (f: any) => f.type === EnumDesignation.SUPPLEMENTARY_FIGURE && Number(f.revision) === Number(article.revision)
    );
    arrayFile.push({
      name: supplementaryFileFigureArray?.length > 0 ? supplementaryFileFigureArray?.length : "no files",
      designation: EnumDesignation.SUPPLEMENTARY_FIGURE,
      files: supplementaryFileFigureArray,
      type: handleFileType(EnumDesignation.SUPPLEMENTARY_FIGURE)?.join(", ").replaceAll(".", ""),
      showAdd: true,
      showSync: supplementaryFileFigureArray?.length === 0 && oldSupplementaryFileFigureArray && isArticleAfterDate,
      showDelete: supplementaryFileFigureArray?.length > 0,
      code: EnumDesignation.SUPPLEMENTARY_FIGURE,
      color: supplementaryFileFigureArray?.length > 0 ? "green" : "black",
    });

    // additional info
    const fileAdditionalInfoArray = fileList?.filter((f: any) => f.designation === EnumDesignation.ADDITIONAL_FILE);
    const oldFileAdditionalInfoArray = article.files?.some(
      (f: any) => f.type === EnumDesignation.ADDITIONAL_FILE && Number(f.revision) === Number(article.revision)
    );
    arrayFile.push({
      name: fileAdditionalInfoArray?.length > 0 ? fileAdditionalInfoArray?.length : "no files",
      designation: EnumDesignation.ADDITIONAL_FILE,
      files: fileAdditionalInfoArray,
      type: handleFileType(EnumDesignation.ADDITIONAL_FILE)?.join(", ").replaceAll(".", ""),
      showAdd: true,
      showSync: fileAdditionalInfoArray?.length === 0 && oldFileAdditionalInfoArray && isArticleAfterDate,
      showDelete: fileAdditionalInfoArray?.length > 0,
      code: EnumDesignation.ADDITIONAL_FILE,
      color: fileAdditionalInfoArray?.length > 0 ? "green" : "black",
    });

    // video
    const fileVideoArray = fileList?.filter((f: any) => f.designation === EnumDesignation.VIDEO_FILE);
    const oldFileVideoArray = article.files?.some(
      (f: any) => f.type === EnumDesignation.VIDEO_FILE && Number(f.revision) === Number(article.revision)
    );
    arrayFile.push({
      name: fileVideoArray?.length > 0 ? fileVideoArray?.length : "no files",
      designation: EnumDesignation.VIDEO_FILE,
      files: fileVideoArray,
      type: handleFileType(EnumDesignation.VIDEO_FILE)?.join(", ").replaceAll(".", ""),
      showAdd: true,
      showSync: fileVideoArray?.length === 0 && oldFileVideoArray && isArticleAfterDate,
      showDelete: fileVideoArray?.length > 0,
      code: EnumDesignation.VIDEO_FILE,
      color: fileVideoArray?.length > 0 ? "green" : "black",
    });

    // tables
    const fileTables = fileList?.find((f: any) => f.designation === EnumDesignation.TABLES);
    const oldFileTablesArray = article.files?.some(
      (f: any) => f.type === EnumDesignation.TABLES && Number(f.revision) === Number(article.revision)
    );
    arrayFile.push({
      id: fileTables?.id ?? null,
      name: fileTables?.name ?? "no files",
      designation: EnumDesignation.TABLES,
      type: handleFileType(EnumDesignation.TABLES)?.join(", ").replaceAll(".", ""),
      location: fileTables?.location ?? null,
      showAdd: !Boolean(fileTables),
      showDelete: Boolean(fileTables),
      showSync: !Boolean(fileTables) && oldFileTablesArray,
      code: EnumDesignation.TABLES,
      color: fileTables ? "green" : "black",
      tooltip: "combine tables into one document",
    });
    setFilesByDesignation(arrayFile);
  };

  useEffect(() => {
    if (filesList) {
      updateFiles(filesList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filesList]);

  const uploadFile = (file: any, values: any) => {
    const newFile = renameFile(file[0], `-R-${(article.revision ?? 0) + 1}-${article.articleId}-${file[0].name}`);
    setUploading(true);
    setBlockUI(true);
    axiosUploadFile(id, [newFile])
      .then((response: any) => {
        const fileNew = {
          id: `file-${filesList.length}`,
          name: response.data.fileName,
          designation: values.designation.code,
          type: values.type,
          location: URL.createObjectURL(newFile),
        };
        setFilesList([...filesList, fileNew]);
      })
      .catch((error: any) => handleErrorMessage(showMessage, error))
      .finally(() => {
        setUploading(false);
        setBlockUI(false);
      });
  };

  const reuseFilesFromOldRevision = (type: string) => {
    confirm({
      description:
        "All files from the previous revision will be copied. You will be allowed to remove or add new files.",
      title: "Reuse files from the previous revision?",
      confirmationText: Labels.YES,
      cancellationText: Labels.NO,
      confirmationButtonProps: {
        variant: "contained",
        color: "primary",
      },
    })
      .then(() => {
        const oldFiles = article.files?.filter(
          (f: any) => f.type === type && Number(f.revision) === Number(article.revision)
        );
        const reuseFiles = oldFiles?.map((f: any, index: number) => ({
          id: `file-reuse-${type}-${index}`,
          name: f.filename,
          designation: f.type,
          location: f.location ?? null,
          type: f.fileType,
        }));
        setFilesList([...filesList, ...reuseFiles]);
      })
      .catch(() => {});
  };

  const reorderFiles = (files: any) => {
    const isExistFigures = files.some((f: any) =>
      [EnumDesignation.FIGURE, EnumDesignation.SUPPLEMENTARY_FIGURE].includes(f.type)
    );
    if (isExistFigures) {
      showModal(FigureReorderDialog, {
        article: {
          ...article,
          files: files,
        },
        onConfirm: submitRevision,
      });
    } else {
      submitRevision({ files });
    }
  };

  const submitRevision = ({ files }: any) => {
    setBlockUI(true);

    const newFiles = files?.map((f: any) => {
      const newF = f;
      delete newF.id;
      return newF;
    });

    axiosPostSubmitRevision(id, {
      articleId: id,
      files: newFiles?.length > 0 ? newFiles : [],
    })
      .then((response: any) => {
        history.push("/manuscript/view");
        showMessage("Files successfully uploaded.", MessageType.SUCCESS);
      })
      .catch((error: any) => handleErrorMessage(showMessage, error))
      .finally(() => setBlockUI(false));
  };

  const submit = () => {
    const files = filesList.map((f: any) => ({
      id: f.id,
      filename: f.name,
      type: f.designation,
      fileType: f.type,
    }));

    reorderFiles(files);
  };

  const deleteFile = (fileId: number) => setFilesList(filesList.filter((file: any) => file.id !== fileId));

  const deleteFileGroup = (code: string) => {
    confirm({
      description: "Are you sure you want delete?",
      title: "Delete",
      confirmationText: Labels.YES,
      cancellationText: Labels.NO,
      confirmationButtonProps: {
        variant: "contained",
        color: "primary",
      },
    })
      .then(() => {
        const fileDelete = filesList?.filter((f: any) => f.designation === code);
        const fileDeleteLocal = fileDelete?.filter((f: any) => isNaN(f.id))?.map(({ id }: any) => id);

        let newFiles = filesList;

        if (fileDeleteLocal?.length > 0) {
          newFiles = newFiles?.filter((f: any) => !fileDeleteLocal.includes(f.id));
        }
        setFilesList(newFiles);
      })
      .catch(() => {});
  };

  const designationOptions: any = [
    { name: "Letter of Rebuttal", code: EnumDesignation.LETTER_OF_REBUTAL },
    { name: "Marked manuscript", code: EnumDesignation.MARKED_MANUSCRIPTS },
    { name: "Unmarked Manuscript", code: EnumDesignation.UNMARKED_MANUSCRIPT },
    { name: "Supplementary figure", code: EnumDesignation.SUPPLEMENTARY_FIGURE },
    { name: "Manuscript", code: EnumDesignation.MANUSCRIPT },
    { name: Labels.FIGURE, code: EnumDesignation.FIGURE },
    { name: Labels.VIDEO_FILE, code: EnumDesignation.VIDEO_FILE },
    { name: Labels.ADDITIONAL_FILE, code: EnumDesignation.ADDITIONAL_FILE },
    { name: Labels.TABLES, code: EnumDesignation.TABLES },
  ];

  const handleOpenDialog = (code: string) => {
    setOpenDialog(true);
    setSelectedDesignation(designationOptions.find((d: any) => d.code === code));
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleResetDialog = (disabled: boolean, setFieldValue: any, files: File[], size: any, type: any) => {
    if (files !== null) setFiles(files);
    setFieldValue("size", size);
    setFieldValue("type", type);
  };

  return {
    uploadFile,
    filesByDesignation,
    filesList,
    designationOptions,
    openDialog,
    handleOpenDialog,
    handleCloseDialog,
    handleResetDialog,
    files,
    uploading,
    submit,
    deleteFile,
    deleteFileGroup,
    selectedDesigantion,
    checkAllRelevantDoc,
    setCheckAllRelevantDoc,
    reuseFilesFromOldRevision,
  };
}

export type { TabUploadRevisionServiceType };
